import type {
  InvestorProfile,
  InvestorProfileAccessStatus,
  InvestorProfileLabel,
} from 'src/tpbp-core/models/investor-profile/investor-profile.types'
import {
  INVESTOR_PROFILE_ACCESS_STATUSES,
  InvestorProfileLabels,
} from 'src/tpbp-core/models/investor-profile/investor-profile.types'
import { mapEsgPaiValuesApi, mapEsgSfdrTaxonomyApi } from '../esg/esg.mapper'
import type {
  InvestorProfileApiLabel,
  MinorInvestorProfileApiScore,
} from './investor-profile.api.types'
import {
  InvestorProfileApiLabels,
  MinorInvestorProfileApiScores,
  type InvestorProfileApiResponse,
} from './investor-profile.api.types'

export const InvestorProfileApiToCoreMinorLabelMapper = {
  [InvestorProfileApiLabels.NONE]: InvestorProfileLabels.NONE,
  [InvestorProfileApiLabels.SECURE]: InvestorProfileLabels.SECURE,
  [InvestorProfileApiLabels.CAUTIOUS]: InvestorProfileLabels.CAUTIOUS,
  [InvestorProfileApiLabels.BALANCED]: InvestorProfileLabels.NONE,
  [InvestorProfileApiLabels.DYNAMIC]: InvestorProfileLabels.NONE,
  [InvestorProfileApiLabels.AGGRESSIVE]: InvestorProfileLabels.NONE,
} as const satisfies Record<InvestorProfileApiLabel, InvestorProfileLabel>

export const InvestorProfileCoreToApiMinorLabelMapper = {
  [InvestorProfileLabels.NONE]: InvestorProfileApiLabels.NONE,
  [InvestorProfileLabels.SECURE]: InvestorProfileApiLabels.SECURE,
  [InvestorProfileLabels.CAUTIOUS]: InvestorProfileApiLabels.CAUTIOUS,
  [InvestorProfileLabels.BALANCED]: InvestorProfileApiLabels.NONE,
  [InvestorProfileLabels.DYNAMIC]: InvestorProfileApiLabels.NONE,
  [InvestorProfileLabels.AGGRESSIVE]: InvestorProfileApiLabels.NONE,
} as const satisfies Record<InvestorProfileLabel, InvestorProfileApiLabel>

export const InvestorProfileCoreToApiMinorScoreMapper = {
  [InvestorProfileLabels.NONE]: MinorInvestorProfileApiScores.NONE,
  [InvestorProfileLabels.SECURE]: MinorInvestorProfileApiScores.SECURE,
  [InvestorProfileLabels.CAUTIOUS]: MinorInvestorProfileApiScores.CAUTIOUS,
  [InvestorProfileLabels.BALANCED]: MinorInvestorProfileApiScores.NONE,
  [InvestorProfileLabels.DYNAMIC]: MinorInvestorProfileApiScores.NONE,
  [InvestorProfileLabels.AGGRESSIVE]: MinorInvestorProfileApiScores.NONE,
} as const satisfies Record<InvestorProfileLabel, MinorInvestorProfileApiScore>

function mapApiToInvestorProfileAccessStatus(
  isUpdatable: boolean,
  requiresUpdate: boolean,
): InvestorProfileAccessStatus {
  if (requiresUpdate) return INVESTOR_PROFILE_ACCESS_STATUSES.MUST_UPDATE
  return isUpdatable
    ? INVESTOR_PROFILE_ACCESS_STATUSES.CAN_UPDATE
    : INVESTOR_PROFILE_ACCESS_STATUSES.DISABLED
}

export function mapApiToInvestorProfile(
  investorProfileApiResponse: InvestorProfileApiResponse,
): InvestorProfile {
  const { data, included } = investorProfileApiResponse
  const esg = included?.find((included) => included.attributes.kind === 'esg')
  const target = data.attributes.risk_interval?.target ?? 0
  const min = data.attributes.risk_interval?.min ?? 0
  const max = data.attributes.risk_interval?.max ?? 0
  return {
    // TODO: https://anatecteam.atlassian.net/browse/TD-994
    label: data.attributes.label,
    score: data.attributes.score,
    piScore: data.attributes.pi_score,
    piLabel: data.attributes.pi_label,
    qceCode: data.attributes.qce_code,
    qceLabel: data.attributes.qce_label,
    esgScore: data.attributes.esg_score,
    esgLabel: data.attributes.esg_label,
    updatedAt: data.attributes.updated_at,
    mustUpdateQualification: data.attributes.requires_update,

    access: {
      status: mapApiToInvestorProfileAccessStatus(
        data.attributes.is_updatable,
        data.attributes.requires_update,
      ),
      piStatus: mapApiToInvestorProfileAccessStatus(
        data.attributes.pi_is_updatable,
        data.attributes.pi_requires_update,
      ),
      qceStatus: mapApiToInvestorProfileAccessStatus(
        data.attributes.qce_is_updatable,
        data.attributes.qce_requires_update,
      ),
      esgStatus: mapApiToInvestorProfileAccessStatus(
        data.attributes.esg_is_updatable,
        data.attributes.esg_requires_update,
      ),
    },
    riskInterval: {
      min,
      max,
      target,
    },
    esg: esg
      ? {
          pai: mapEsgPaiValuesApi(esg.attributes.esg_criteria.esg_pai),
          sfdr: mapEsgSfdrTaxonomyApi(esg.attributes.esg_criteria.esg_sfdr),
          taxonomy: mapEsgSfdrTaxonomyApi(
            esg.attributes.esg_criteria.esg_taxonomy,
          ),
        }
      : {},
  }
}
